import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import { cloneElement, useId, useState, type ReactElement, type ReactNode } from 'react';

import { testIdAttr } from '@/lib/testIds';

interface DialogProps {
  /**
   * The element that opens the dialog. Receives an onClick handler. Not needed
   * when the caller supplies `open`, because then something else decides.
   */
  trigger?: ReactElement<{ onClick?: () => void }>;
  /**
   * Supply this to take charge of whether the dialog is on screen. Leave it out
   * and the dialog opens itself from `trigger`.
   */
  open?: boolean;
  /** Called when the dialog asks to close: the X, Escape or the backdrop. */
  onClose?: () => void;
  title: string;
  /** Read out to screen readers alongside the title. */
  description?: string;
  children?: ReactNode;
  /** Buttons shown along the bottom. */
  actions?: ReactNode;
  /** How wide the dialog is allowed to grow. */
  maxWidth?: 'xs' | 'sm' | 'md';
  /**
   * Fill the screen at every width, not only on a phone: for a dialog that
   * holds a page of its own, such as the ARC's Preview.
   */
  fullScreen?: boolean;
  /**
   * False hides the close X. A dialog that must be answered one way or the
   * other has no X, because an X is a silent third answer.
   */
  dismissible?: boolean;
  /**
   * Names the dialog for the testing team's tests. It goes on the panel that
   * has `role="dialog"`; the title, description and close X get `-title`,
   * `-description` and `-close-button`. See docs/test-ids.md.
   */
  testId: string;
}

/**
 * A modal dialog built on the MUI primitive.
 *
 * MUI supplies the behaviour that is tedious and easy to get wrong: focus moves
 * into the dialog on open and back to the trigger on close, Tab is trapped
 * inside it, Escape closes it, and the page behind is hidden from screen
 * readers. Those are WCAG 2.1 AA requirements, so they are not worth
 * reimplementing by hand.
 *
 * It works two ways. Left alone it owns whether it is open and opens from its
 * trigger, which suits a dialog that only shows something. Given `open` and
 * `onClose` the caller owns it, which a dialog holding a form needs: the form
 * has to stay open when a save fails and close when one succeeds, and only the
 * thing doing the saving knows which happened.
 *
 * Below the `sm` breakpoint it fills the screen. A centred dialog taller than a
 * phone's window cannot be finished: its buttons sit below the fold of a box
 * that does not scroll. Full-screen, the title and the buttons stay put and the
 * content between them scrolls.
 */
export function Dialog({
  trigger,
  open: openProp,
  onClose,
  title,
  description,
  children,
  actions,
  maxWidth = 'sm',
  fullScreen = false,
  dismissible = true,
  testId,
}: DialogProps) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [selfOpen, setSelfOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = openProp ?? selfOpen;

  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  function close() {
    if (controlled) onClose?.();
    else setSelfOpen(false);
  }

  return (
    <>
      {trigger ? cloneElement(trigger, { onClick: () => setSelfOpen(true) }) : null}

      <MuiDialog
        open={open}
        // Escape and the backdrop route through the same close as the X, so a
        // controlled caller hears about every way out rather than only one.
        onClose={close}
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        fullWidth
        maxWidth={maxWidth}
        fullScreen={fullScreen || phone}
        // The panel, not the outer layer: the panel is what has role="dialog",
        // and what a test scopes into to find the dialog's own controls.
        slotProps={{ paper: testIdAttr(testId) }}
      >
        <DialogTitle
          id={titleId}
          data-testid={`${testId}-title`}
          // Titles quote names, which may be long and unbroken (B28).
          sx={{ pr: dismissible ? 6 : 3, overflowWrap: 'anywhere' }}
        >
          {title}
        </DialogTitle>

        {dismissible ? (
          <IconButton
            onClick={close}
            aria-label="Close"
            data-testid={`${testId}-close-button`}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}

        <DialogContent>
          {description ? (
            <DialogContentText
              id={descriptionId}
              data-testid={`${testId}-description`}
              sx={{ mb: 2, overflowWrap: 'anywhere' }}
            >
              {description}
            </DialogContentText>
          ) : null}
          {children}
        </DialogContent>

        {actions ? <DialogActions>{actions}</DialogActions> : null}
      </MuiDialog>
    </>
  );
}
